class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form.search-form');
    this.input = this.form.querySelector('input[type="search"]');
    this.resetButton = this.form.querySelector('button[type="reset"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.loadingIndicator = this.form.querySelector('.loading-spinner-wrapper');
    this.resultsContainer = this.querySelector('#predictive-search-results');
    this.status = this.querySelector('#predictive-search-status');
    this.inputTimeout = null;
    this.currentIndex = -1;

    this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    this.input.addEventListener('blur', this.onInputBlur.bind(this));
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.resetButton.addEventListener('click', this.handleReset.bind(this));

    this.resultsContainer.addEventListener('click', (event) => {
      if (event.target.closest('#predictive-search-search-for-button')) {
        this.form.submit();
      }
    });

    const dialog = this.closest('dialog');

    if (dialog) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'open' && dialog.open) {
            window.setTimeout(() => this.input.focus(), 100);
          }
        });
      });

      observer.observe(dialog, { attributes: true });
    }
  }

  async onInputFocus() {
    let index = 0;
    const focusText = this.input.dataset.textFocus || this.input.placeholder;

    this.input.placeholder = '';
    window.clearTimeout(this.inputTimeout);

    await new Promise((resolve) => {
      window.setTimeout(resolve, 200);
    });

    const type = () => {
      if (index < focusText.length) {
        this.input.placeholder += focusText.charAt(index);
        index += 1;
        this.inputTimeout = window.setTimeout(type, 30);
      }
    };

    type();
  }

  onInputBlur() {
    window.clearTimeout(this.inputTimeout);
    this.input.placeholder = this.input.dataset.textBlur;
  }

  handleInput(event) {
    const query = event.target.value.trim();

    if (query.length) {
      this.resetButton.removeAttribute('hidden');
      this.fetchSuggestions(query);
      return;
    }

    this.resetButton.setAttribute('hidden', 'hidden');
    this.clearSuggestions();
  }

  handleReset() {
    window.setTimeout(() => {
      this.input.focus();
      this.input.dispatchEvent(new Event('input'));
    });
  }

  async fetchSuggestions(query) {
    const url = `${this.dataset.route}?q=${encodeURIComponent(query)}&resources[type]=${this.dataset.resourceTypes}&resources[limit]=${this.dataset.limit}&section_id=predictive-search`;

    try {
      this.setLoading(true);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const text = await response.text();
      this.renderSuggestions(text);
    } catch (error) {
      this.clearSuggestions();
      console.error('Error fetching predictive search suggestions:', error);
    } finally {
      this.setLoading(false);
    }
  }

  renderSuggestions(text) {
    const section = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('#shopify-section-predictive-search');

    if (!section) {
      this.clearSuggestions();
      return;
    }

    this.resultsContainer.innerHTML = section.innerHTML;
    this.resultsContainer.removeAttribute('hidden');
    this.input.setAttribute('aria-expanded', 'true');
    this.currentIndex = -1;

    const count = this.resultsContainer.querySelector('[data-predictive-search-count]');

    if (count) {
      this.status.textContent = count.textContent.trim();
    }

    this.adjustMobileLayout();
  }

  clearSuggestions() {
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.setAttribute('hidden', 'hidden');
    this.resultsContainer.removeAttribute('aria-busy');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.status.textContent = '';
    this.currentIndex = -1;
  }

  adjustMobileLayout() {
    if (!window.matchMedia('(max-width: 799px)').matches) {
      return;
    }

    const products = this.querySelector('#predictive-search-products');

    if (!products) {
      return;
    }

    ['pages', 'articles', 'collections'].forEach((type) => {
      const group = this.querySelector(`#predictive-search-${type}`);

      if (group) {
        products.insertAdjacentElement('afterend', group);
      }
    });
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.loadingIndicator.removeAttribute('hidden');
      this.submitButton.setAttribute('hidden', 'hidden');
      this.resultsContainer.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.textLoading;
      return;
    }

    this.loadingIndicator.setAttribute('hidden', 'hidden');
    this.submitButton.removeAttribute('hidden');
    this.resultsContainer.removeAttribute('aria-busy');
  }

  handleKeyDown(event) {
    const focusableElements = Array.from(this.resultsContainer.querySelectorAll('a, button'));

    if (!focusableElements.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.currentIndex = (this.currentIndex + 1) % focusableElements.length;
      this.updateActiveOption(focusableElements);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.currentIndex = (this.currentIndex - 1 + focusableElements.length) % focusableElements.length;
      this.updateActiveOption(focusableElements);
    } else if (event.key === 'Escape') {
      this.clearSuggestions();
      this.input.focus();
    }
  }

  updateActiveOption(focusableElements) {
    focusableElements.forEach((element, index) => {
      const isActive = index === this.currentIndex;
      const option = element.closest('[role="option"]');

      if (!option) {
        return;
      }

      option.setAttribute('aria-selected', String(isActive));
      element.tabIndex = isActive ? 0 : -1;

      if (isActive) {
        element.focus();
        this.input.setAttribute('aria-activedescendant', option.id);
      }
    });
  }

  debounce(func, wait) {
    let timeout;

    return function executedFunction(...args) {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => func(...args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
